import type { Brand } from '../brand';
import { assertSafeInteger } from '../integers';

/**
 * OPEN WORLD randomness - a THIRD domain, keyed and secret.
 *
 * ## Why it is neither of the other two
 *
 * Not SIMULATION: a simulation seed is handed to the browser on purpose, and a
 * world whose seed the client held would be a world every boss, planet and
 * comet could be datamined out of before anyone sailed anywhere. Not ECONOMIC
 * either: nothing here decides a reward, a drop, a jewel or a coin, and an
 * economic draw is deliberately non-reproducible - it draws fresh entropy and
 * stores the answer - while a world must be re-derivable from four values
 * forever.
 *
 * So: reproducible like the simulation, secret like the economy. The nominal
 * brand is the enforcement - an {@link OpenWorldSeed} is not assignable to a
 * simulation parameter, a `SimulationSeed` is not assignable here, and neither
 * reaches a `LootSeed`.
 *
 * ## The construction
 *
 * `HMAC-SHA-256(secret, label)`, in counter mode. The label is built by the
 * generator from `(worldId, grid version, content version, coordinate,
 * stream)`; the counter appends `#0`, `#1`, ... when a stream needs more than
 * thirty-two bytes. There is no stateful generator, and that is the whole
 * design: every value in the world is a pure function of its own address, so
 * nothing depends on iteration order, on how many quadrants were built first,
 * or on whether the process restarted halfway.
 *
 * HMAC rather than a fast mixer because the seed is secret and the outputs are
 * visible: a player who sails far enough sees a great many derived values, and
 * a reversible mix would eventually give up the key - and with it the rest of
 * the map.
 *
 * ## Independent streams, and what that buys
 *
 * Each subsystem draws from its own labelled stream. Adding a species of fish
 * next year means adding a stream, and a stream that nobody drew from before
 * cannot shift the bytes any other stream consumed. Positions of bosses and
 * planets already generated stay exactly where they are.
 *
 * ## Where the secret lives
 *
 * One column, server side (`open_world_worlds.secret_seed`). Never in a
 * response, never in a log, never in a snapshot, never in this package's
 * bundle - this module defines the TYPE and the derivation, and holds no key.
 * Same arrangement as `LootSeed` in `./loot`.
 *
 * @see docs/SECURITY.md
 */

/**
 * Web Crypto and `TextEncoder`, declared narrowly rather than pulled in through
 * `lib.dom` or `@types/node`: this package compiles with `types: []`, and both
 * globals exist in every runtime the project targets. Same approach as
 * `digest.ts` and `loot.ts`.
 */
declare const crypto: {
  readonly subtle: {
    importKey(
      format: 'raw',
      keyData: ArrayBufferView | ArrayBuffer,
      algorithm: { name: 'HMAC'; hash: string },
      extractable: boolean,
      usages: readonly string[],
    ): Promise<CryptoKeyLike>;
    sign(
      algorithm: 'HMAC',
      key: CryptoKeyLike,
      data: ArrayBufferView | ArrayBuffer,
    ): Promise<ArrayBuffer>;
  };
};
interface CryptoKeyLike {
  readonly type: string;
}
declare const TextEncoder: {
  new (): { encode(input: string): Uint8Array };
};

/** The world's secret key, 64 lowercase hex characters. Server memory and one column. */
export type OpenWorldSeed = Brand<string, 'OpenWorldSeed'>;

/** Marks a hex string as a world seed. Never accept one from a request. */
export function openWorldSeed(hex: string): OpenWorldSeed {
  if (!/^[0-9a-f]{64}$/.test(hex)) {
    throw new RangeError('an open world seed must be 64 lowercase hex characters (256 bits)');
  }
  return hex as OpenWorldSeed;
}

/**
 * Every purpose a world seed may serve, one per subsystem.
 *
 * An allow-list, not documentation. A new subsystem appends a label; it never
 * renames or reorders one, because a label IS the derivation - renaming
 * `open-world-boss-v1` would move every boss in every world.
 *
 * The `-v1` suffix is how a subsystem re-rolls only itself: bumping a single
 * stream to `-v2` moves that subsystem's spaces and leaves the rest untouched,
 * which the world-wide content version cannot do.
 */
export const OPEN_WORLD_STREAMS = [
  /** The quadrant's danger rating, 1..5. */
  'open-world-danger-v1',
  /** Its resource rating, drawn independently of danger. */
  'open-world-resources-v1',
  /** How many water areas, and where. */
  'open-world-water-v1',
  /** How many mining planets, and where. */
  'open-world-planet-v1',
  /** How many comets, and where. */
  'open-world-comet-v1',
  /** How many boss spaces, and where. */
  'open-world-boss-v1',
  /** How many mob spaces, and where. */
  'open-world-mob-v1',
  /**
   * **A joia secreta de um cometa** (gate V28I.I).
   *
   * Um fluxo PROPRIO, ao lado do que decide onde os cometas estao. Sao dois
   * fluxos com a mesma chave e mensagens diferentes: quem conhecesse as
   * posicoes nao conheceria as joias, porque um nao se deriva do outro.
   *
   * A etiqueta completa acrescenta o cometa e a GERACAO, que e o que faz cada
   * renascimento trazer uma rolagem nova sem re-rolar as outras rochas.
   */
  'open-world-comet-jewel-v1',
  /**
   * **O conteudo secreto de um planeta** (gate V28I.J).
   *
   * Um fluxo proprio, ao lado do que decide ONDE os planetas estao. A etiqueta
   * completa acrescenta o planeta, a GERACAO e o nome do subfluxo — silhueta,
   * ouro, nucleos, itens — portanto quem conhecesse um nao derivaria os outros.
   */
  'open-world-planet-content-v1',
  /**
   * **O conteudo secreto de um planeta, com as nove categorias** (V28I.J.1).
   *
   * A `v1` sorteava de quatro categorias; esta sorteia das nove que a carga
   * de campo passou a transportar. Um fluxo NOVO e nao uma lista trocada:
   * uma geracao selada com a v1 nao pode ser reinterpretada com a lista de
   * hoje, e a etiqueta diferente e o que o torna impossivel.
   *
   * A v1 fica registada porque as geracoes que ela produziu continuam a
   * existir — guardadas, e lidas do registo em vez de re-derivadas.
   */
  'open-world-planet-content-v2',
  /**
   * **Que identidade cada vaga de uma faixa usa** (gate V28I.BW.5A).
   *
   * A etiqueta completa acrescenta o dominio — alienigena ou chefe — e o grau
   * de perigo, e NAO leva coordenada: a permutacao e da FAIXA inteira, e uma
   * que variasse por quadrante nao teria como garantir que as quatro
   * identidades aparecem no mundo.
   *
   * Dez derivacoes cobrem o mundo todo: duas familias por cinco graus.
   */
  'open-world-map-band-v1',
] as const;

export type OpenWorldStream = (typeof OPEN_WORLD_STREAMS)[number];

export function isOpenWorldStream(value: string): value is OpenWorldStream {
  return (OPEN_WORLD_STREAMS as readonly string[]).includes(value);
}

const HMAC_BYTES = 32;
const LANE_BYTES = 8;
const LANES_PER_BLOCK = HMAC_BYTES / LANE_BYTES;
const MASK_64 = (1n << 64n) - 1n;

/**
 * `HMAC-SHA-256(secret, "<label>#<block>")` - thirty-two bytes of stream.
 *
 * Counter mode, so a stream is unbounded without ever becoming stateful: block
 * seven is derivable without deriving blocks zero to six, and two processes
 * asking for the same block get the same bytes.
 */
export async function openWorldStreamBlock(
  seed: OpenWorldSeed,
  label: string,
  blockIndex: number,
): Promise<Uint8Array> {
  assertSafeInteger(blockIndex, 'open world block index');
  if (blockIndex < 0) {
    throw new RangeError(`open world block index must not be negative, received ${blockIndex}`);
  }
  const keyBytes = new Uint8Array(
    (seed.match(/../g) ?? []).map((byte) => Number.parseInt(byte, 16)),
  );
  const key = await crypto.subtle.importKey(
    'raw',
    keyBytes,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const signature = await crypto.subtle.sign(
    'HMAC',
    key,
    new TextEncoder().encode(`${label}#${blockIndex}`),
  );
  return new Uint8Array(signature);
}

/**
 * A cursor over one stream.
 *
 * Async because the bytes come from Web Crypto, and deliberately so: forcing
 * the async shape stops a synchronous, non-cryptographic stand-in from ever
 * satisfying it by accident. It is still pure - the same seed and label always
 * produce the same sequence, however many cursors are alive at once.
 */
export interface OpenWorldDraw {
  readonly label: string;
  /** How many values have been produced. Rejected candidates do not count. */
  readonly drawCount: number;
  /** Uniform integer in `[min, max]`, rejection-sampled so it is unbiased. */
  nextIntInclusive(min: number, max: number): Promise<number>;
}

export function createOpenWorldDraw(seed: OpenWorldSeed, label: string): OpenWorldDraw {
  let block: Uint8Array | null = null;
  let blockIndex = -1;
  let laneInBlock = LANES_PER_BLOCK;
  let draws = 0;

  const nextLane = async (): Promise<bigint> => {
    if (block === null || laneInBlock >= LANES_PER_BLOCK) {
      blockIndex += 1;
      block = await openWorldStreamBlock(seed, label, blockIndex);
      laneInBlock = 0;
    }
    const offset = laneInBlock * LANE_BYTES;
    laneInBlock += 1;
    let value = 0n;
    for (let byte = 0; byte < LANE_BYTES; byte += 1) {
      value = (value << 8n) | BigInt(block[offset + byte] ?? 0);
    }
    return value & MASK_64;
  };

  return {
    label,
    get drawCount() {
      return draws;
    },
    async nextIntInclusive(min: number, max: number): Promise<number> {
      assertSafeInteger(min, 'open world draw min');
      assertSafeInteger(max, 'open world draw max');
      if (max < min) {
        throw new RangeError(`invalid range [${min}, ${max}]`);
      }
      if (!Number.isSafeInteger(max - min)) {
        throw new RangeError(`range [${min}, ${max}] is wider than Number can represent exactly`);
      }
      const span = BigInt(max - min) + 1n;
      // Rejection sampling: plain modulo would bias the low end of the range.
      const limit = MASK_64 - (MASK_64 % span);
      let candidate = await nextLane();
      while (candidate >= limit) {
        candidate = await nextLane();
      }
      draws += 1;
      return min + Number(candidate % span);
    },
  };
}
